<template>
  <b-nav class="main-menu" pills vertical ref="mainMenu" :class="{'menu-collapsed': isCollapsed}">
    <template v-for="menuItem in menuItems">
      <li v-permission="menuItem.permission" v-if="showItem(menuItem) && menuItem.children.length > 0" class="nav-item"
          :key="`li-${menuItem.uuid}`">
        <ul class="dropdown-wrapper">
          <b-nav-item active-class="active" :aria-expanded="collapseMap[`dropdown_${menuItem.uuid}`] ? 'true' : 'false'"
                      :class="collapseMap[`dropdown_${menuItem.uuid}`] ? 'collapsed' : 'not-collapsed'"
                      :aria-controls="`dropdown_${menuItem.uuid}`" @click="dropdownClick(menuItem.uuid)">
          <span class="menu-item-icon">
            <font-awesome-icon v-if="menuItem.icon" :icon="menuItem.icon"></font-awesome-icon>
          </span>
            <span class="menu-text">
            {{ menuItem.text }}
          </span>
          </b-nav-item>
          <li class="inner-dropdown">
            <b-collapse :id="`dropdown_${menuItem.uuid}`" v-model="collapseMap[`dropdown_${menuItem.uuid}`]">
              <ul>
                <b-nav-item v-for="childMenuItem in menuItem.children" :to="childMenuItem.path"
                            v-permission="childMenuItem.permission"
                            :key="childMenuItem.path" :exact="false" active-class="active">
                <span class="menu-item-icon">
                  <font-awesome-icon v-if="childMenuItem.icon" :icon="childMenuItem.icon"></font-awesome-icon>
                </span>
                  <span class="menu-text">{{ childMenuItem.text }}</span>
                </b-nav-item>
              </ul>
            </b-collapse>
          </li>
        </ul>
      </li>
      <b-nav-item v-permission="menuItem.permission" v-else-if="showItem(menuItem)" active-class="active" :to="menuItem.path"
                  :key="`nav-item=${menuItem.uuid}`">
        <span class="menu-item-icon">
          <font-awesome-icon v-if="menuItem.icon" :icon="menuItem.icon"></font-awesome-icon>
        </span>
        <span class="menu-text">{{ menuItem.text }}</span>
      </b-nav-item>
    </template>

    <img v-if="isXmas" src="@/assets/menu_xmas_gnomes.png" style="width:155px;position: fixed;bottom: 33px"/>

    <b-nav-item class="feature-request-placeholder"
                v-permission="'view_featurerequest'" v-else active-class="active" :to="{'name': 'feature_requests'}">
      <span class="menu-item-icon">
        <font-awesome-icon icon="lightbulb"></font-awesome-icon>
      </span>
      <span class="menu-text">{{ $t('Feature Request') }}</span>
    </b-nav-item>

    <li class="version-placeholder nav-item">
      <span @click="onVersionClick">{{ $t("Version") }}: {{ computedVersion }}</span>
      <font-awesome-icon icon="newspaper" class="release-notes icon" v-if="releaseNoteAvailable"
                         @click="showReleaseNotesListModal"/>
    </li>
  </b-nav>
</template>

<script>
  import menuService from './MenuService';
  import {createNamespacedHelpers} from "vuex";
  import {version} from "../../../../package.json";

  const {mapState, mapActions} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "Menu",
    data: function () {
      let menuItems = menuService.getItems();
      const {collapseMap, collapsableData} = this.initCollapseMap(menuItems);

      return {
        menuItems: menuItems,
        collapsableData: collapsableData,
        collapseMap: collapseMap,
        showCollapse: true,
        appVersion: version,
        isXmas: false
      }
    },
    methods: {
      ...mapActions(['updateMenuCollapsed', 'showReleaseNoteModalForce', 'showReleaseNotesListModal']),
      initCollapseMap(menuItems) {
        let collapseMap = {};
        let collapsableData = {};

        menuItems.forEach(menuItem => {
          menuItem.extraPathsToLeaveCollapsed.forEach(path => collapsableData[path] = menuItem.uuid)
          menuItem.children.forEach((item) => collapsableData[item.path] = menuItem.uuid);
        })

        if (this.$route.path in collapsableData && collapsableData[this.$route.path]) {
          collapseMap[`dropdown_${collapsableData[this.$route.path]}`] = true;
        }

        return {collapseMap, collapsableData};
      },
      dropdownClick(index) {
        this.collapseMap[`dropdown_${index}`] = !this.collapseMap[`dropdown_${index}`];
      },
      onVersionClick() {
        if (this.releaseNoteAvailable) {
          this.showReleaseNoteModalForce()
        }
      },
      showItem(menuItem) {
        return !menuItem.show || menuItem.show();
      }
    },
    watch: {
      $route: function () {
        Object.keys(this.collapseMap).forEach((key) => {
          this.collapseMap[key] = false;
        })

        if (this.$route.path in this.collapsableData) {
          this.collapseMap[`dropdown_${this.collapsableData[this.$route.path]}`] = true;
        }

        if (this.$isMobileDevice()) {
          this.updateMenuCollapsed(true);
        }
      }
    },
    computed: {
      ...mapState({
        isCollapsed: state => state.menu.collapsed,
        releaseNoteInfo: state => state.releaseNoteInfo
      }),
      releaseNoteAvailable() {
        return this.releaseNoteInfo && this.releaseNoteInfo.version && this.releaseNoteInfo.date
      },
      computedVersion() {
        if (this.releaseNoteAvailable) {
          return this.releaseNoteInfo.version
        }
        return this.appVersion
      }
    }
  }
</script>

<style lang="scss">
  @import "./../../scss/variables";

  .main-menu {
    font-size: .95em;
    background-color: $menu-bg;
    width: $menu-width;
    transition: all ease-in-out .3s;
    flex-wrap: nowrap;
    display: block !important;
    overflow-x: hidden;
    overflow-y: auto;
    margin-bottom: 36px !important;

    .version-placeholder {
      height: 35px;
      padding: $menu-nav-link-padding;
      border-top: 1px solid $menu-bg;
      position: fixed;
      bottom: 0;
      width: $menu-width;
      color: $menu-nav-link-color;
      background: $menu-bg;
    }

    .version-placeholder:hover {
      text-decoration: underline;
      cursor: pointer;
    }

    .feature-request-placeholder {
      bottom: 33px;
      width: $menu-width;
      position: fixed;
    }

    .release-notes {
      height: 27px;
      border-top: 1px solid $menu-bg;
      position: fixed;
      bottom: 5px;
      left: 100px;
      width: 50px;
      color: $menu-nav-link-color;
      padding: 2px;
    }

    .release-notes:hover {
      cursor: pointer;
    }

    &.menu-collapsed {
      width: 0;
    }

    .nav-item {
      .nav-link {
        border-radius: 0;
        white-space: nowrap;
        padding: $menu-nav-link-padding;
        color: $menu-nav-link-color;

        &:hover {
          color: $menu-nav-link-hover-color;
          background-color: $menu-nav-link-hover-bg-color;
        }

        &.active {
          background-color: $menu-nav-link-active-bg-color;
          color: $menu-nav-link-active-color;
        }

        .menu-item-icon {
          display: inline-block;
          margin-right: $menu-menu-item-icon-margin-right;
          min-width: $menu-menu-item-icon-min-width;
          text-align: $menu-menu-item-icon-text-align;
        }
      }

      .dropdown-wrapper {
        list-style: none;
        padding: 0;

        .nav-item {
          &.collapsed {
            .nav-link {
              color: $dark;
              background-color: $menu-dropdown-div-nav-item-collapsed-bg-color;
              padding: $menu-nav-link-padding;
            }
          }
        }

        .inner-dropdown {
          ul {
            list-style: none;
            padding: 0;

            .nav-item {
              .nav-link {
                background-color: $menu-dropdown-div-nav-item-bg-color;
                color: $menu-dropdown-div-nav-item-color;
                padding-left: $menu-dropdown-div-nav-item-padding-left;

                &:hover {
                  color: $menu-dropdown-div-nav-item-hover-color;
                  background-color: $menu-dropdown-div-nav-item-hover-bg-color;
                }
              }

              .nav-link.router-link-exact-active.active, .nav-link.active {
                background-color: $menu-dropdown-div-nav-item-active-color;
              }
            }
          }
        }
      }
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
    .main-menu {
      position: absolute;
      top: 3.25em;
      bottom: 0;
      left: 0;
      z-index: 50;
      transition: all ease-in-out .3s;

      &.menu-collapsed {
        width: $menu-width;
        left: -$menu-width;
      }
    }
  }
</style>
